// src/hospital/models/index.js
// Frontend models for all Hospital Management modules
// ─────────────────────────────────────────────────────────────────────────────

// ── PATIENT ────────────────────────────────────────────────────────────────────
export class Patient {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.uhid = d.uhid || '';
    this.firstName = d.firstName || '';
    this.lastName = d.lastName || '';
    this.fullName = d.fullName || `${d.firstName || ''} ${d.lastName || ''}`.trim();
    this.dob = d.dob || '';
    this.gender = d.gender || '';
    this.bloodGroup = d.bloodGroup || '';
    this.phone = d.phone || '';
    this.email = d.email || '';
    this.address = d.address || '';
    this.city = d.city || '';
    this.state = d.state || '';
    this.emergencyContact = d.emergencyContact || '';
    this.emergencyPhone = d.emergencyPhone || '';
    this.maritalStatus = d.maritalStatus || '';
    this.occupation = d.occupation || '';
    this.religion = d.religion || '';
    this.nationality = d.nationality || 'Indian';
    this.allergies = d.allergies || '';
    this.medicalHistory = d.medicalHistory || '';
    this.insuranceProvider = d.insuranceProvider || '';
    this.insuranceNumber = d.insuranceNumber || '';
    this.status = d.status || 'Active';
    this.registrationDate = d.registrationDate || new Date().toISOString();
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
    this.updatedAt = d.updatedAt || null;
  }
  get age() {
    if (!this.dob) return '—';
    return Math.floor((Date.now() - new Date(this.dob)) / 3.156e10);
  }
  validate() {
    const e = [];
    if (!this.firstName?.trim()) e.push('First name is required');
    if (!this.lastName?.trim())  e.push('Last name is required');
    if (!this.phone?.trim())     e.push('Phone is required');
    if (!this.gender)            e.push('Gender is required');
    if (!this.dob)               e.push('Date of birth is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Patient(d) : null; }
}

// ── DOCTOR ─────────────────────────────────────────────────────────────────────
export class Doctor {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.employeeId = d.employeeId || '';
    this.firstName = d.firstName || '';
    this.lastName = d.lastName || '';
    this.fullName = d.fullName || `Dr. ${d.firstName || ''} ${d.lastName || ''}`.trim();
    this.specialization = d.specialization || '';
    this.qualification = d.qualification || '';
    this.experience = d.experience || 0;
    this.phone = d.phone || '';
    this.email = d.email || '';
    this.department = d.department || '';
    this.departmentId = d.departmentId || null;
    this.registrationNumber = d.registrationNumber || '';
    this.consultationFee = d.consultationFee || 0;
    this.availableDays = d.availableDays || [];
    this.startTime = d.startTime || '';
    this.endTime = d.endTime || '';
    this.status = d.status || 'Active';
    this.photo = d.photo || null;
    this.address = d.address || '';
    this.joiningDate = d.joiningDate || '';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.firstName?.trim())        e.push('First name is required');
    if (!this.lastName?.trim())         e.push('Last name is required');
    if (!this.specialization?.trim())   e.push('Specialization is required');
    if (!this.phone?.trim())            e.push('Phone is required');
    if (!this.department?.trim())       e.push('Department is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Doctor(d) : null; }
}

// ── NURSE ──────────────────────────────────────────────────────────────────────
export class Nurse {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.employeeId = d.employeeId || '';
    this.firstName = d.firstName || '';
    this.lastName = d.lastName || '';
    this.fullName = d.fullName || `${d.firstName || ''} ${d.lastName || ''}`.trim();
    this.qualification = d.qualification || '';
    this.experience = d.experience || 0;
    this.phone = d.phone || '';
    this.email = d.email || '';
    this.department = d.department || '';
    this.ward = d.ward || '';
    this.shift = d.shift || 'Morning';
    this.specialization = d.specialization || '';
    this.status = d.status || 'Active';
    this.joiningDate = d.joiningDate || '';
    this.salary = d.salary || 0;
    this.companyId = d.companyId || null;
  }
  validate() {
    const e = [];
    if (!this.firstName?.trim())  e.push('First name is required');
    if (!this.lastName?.trim())   e.push('Last name is required');
    if (!this.phone?.trim())      e.push('Phone is required');
    if (!this.department?.trim()) e.push('Department is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Nurse(d) : null; }
}

// ── APPOINTMENT ────────────────────────────────────────────────────────────────
export class Appointment {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.appointmentNumber = d.appointmentNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.patientPhone = d.patientPhone || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.departmentId = d.departmentId || null;
    this.department = d.department || '';
    this.appointmentDate = d.appointmentDate || '';
    this.appointmentTime = d.appointmentTime || '';
    this.type = d.type || 'OPD';
    this.priority = d.priority || 'Normal';
    this.symptoms = d.symptoms || '';
    this.notes = d.notes || '';
    this.status = d.status || 'Scheduled';
    this.fee = d.fee || 0;
    this.paymentStatus = d.paymentStatus || 'Unpaid';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)           e.push('Patient is required');
    if (!this.doctorId)            e.push('Doctor is required');
    if (!this.appointmentDate)     e.push('Date is required');
    if (!this.appointmentTime)     e.push('Time is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Appointment(d) : null; }
}

// ── DEPARTMENT ─────────────────────────────────────────────────────────────────
export class Department {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.name = d.name || '';
    this.code = d.code || '';
    this.description = d.description || '';
    this.headDoctorId = d.headDoctorId || null;
    this.headDoctorName = d.headDoctorName || '';
    this.totalBeds = d.totalBeds || 0;
    this.availableBeds = d.availableBeds || 0;
    this.phone = d.phone || '';
    this.location = d.location || '';
    this.status = d.status || 'Active';
    this.companyId = d.companyId || null;
  }
  validate() {
    const e = [];
    if (!this.name?.trim()) e.push('Department name is required');
    if (!this.code?.trim()) e.push('Department code is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Department(d) : null; }
}

// ── WARD ───────────────────────────────────────────────────────────────────────
export class Ward {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.wardNumber = d.wardNumber || '';
    this.name = d.name || '';
    this.type = d.type || 'General';
    this.departmentId = d.departmentId || null;
    this.department = d.department || '';
    this.floor = d.floor || '';
    this.totalBeds = d.totalBeds || 0;
    this.availableBeds = d.availableBeds || 0;
    this.chargePerDay = d.chargePerDay || 0;
    this.nurseInCharge = d.nurseInCharge || '';
    this.facilities = d.facilities || [];
    this.status = d.status || 'Active';
    this.companyId = d.companyId || null;
  }
  validate() {
    const e = [];
    if (!this.name?.trim())       e.push('Ward name is required');
    if (!this.wardNumber?.trim()) e.push('Ward number is required');
    if (!this.type)               e.push('Ward type is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Ward(d) : null; }
}

// ── BED ────────────────────────────────────────────────────────────────────────
export class Bed {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.bedNumber = d.bedNumber || '';
    this.wardId = d.wardId || null;
    this.wardName = d.wardName || '';
    this.type = d.type || 'General';
    this.floor = d.floor || '';
    this.chargePerDay = d.chargePerDay || 0;
    this.status = d.status || 'Available';
    this.currentPatientId = d.currentPatientId || null;
    this.currentPatientName = d.currentPatientName || '';
    this.admissionDate = d.admissionDate || null;
    this.features = d.features || [];
    this.companyId = d.companyId || null;
  }
  validate() {
    const e = [];
    if (!this.bedNumber?.trim()) e.push('Bed number is required');
    if (!this.wardId)            e.push('Ward is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Bed(d) : null; }
}

// ── OPD ────────────────────────────────────────────────────────────────────────
export class OPDVisit {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.visitNumber = d.visitNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.department = d.department || '';
    this.visitDate = d.visitDate || new Date().toISOString().split('T')[0];
    this.visitTime = d.visitTime || '';
    this.chiefComplaints = d.chiefComplaints || '';
    this.diagnosis = d.diagnosis || '';
    this.prescription = d.prescription || '';
    this.followUpDate = d.followUpDate || '';
    this.fee = d.fee || 0;
    this.paymentStatus = d.paymentStatus || 'Unpaid';
    this.status = d.status || 'Scheduled';
    this.vitals = d.vitals || {};
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId) e.push('Patient is required');
    if (!this.doctorId)  e.push('Doctor is required');
    if (!this.visitDate) e.push('Visit date is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new OPDVisit(d) : null; }
}

// ── IPD (Admission) ────────────────────────────────────────────────────────────
export class IPDAdmission {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.admissionNumber = d.admissionNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.wardId = d.wardId || null;
    this.wardName = d.wardName || '';
    this.bedId = d.bedId || null;
    this.bedNumber = d.bedNumber || '';
    this.admissionDate = d.admissionDate || '';
    this.admissionTime = d.admissionTime || '';
    this.dischargeDate = d.dischargeDate || null;
    this.admissionType = d.admissionType || 'General';
    this.diagnosis = d.diagnosis || '';
    this.referredBy = d.referredBy || '';
    this.status = d.status || 'Admitted';
    this.totalAmount = d.totalAmount || 0;
    this.paidAmount = d.paidAmount || 0;
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  get balance() { return this.totalAmount - this.paidAmount; }
  validate() {
    const e = [];
    if (!this.patientId)     e.push('Patient is required');
    if (!this.doctorId)      e.push('Doctor is required');
    if (!this.wardId)        e.push('Ward is required');
    if (!this.admissionDate) e.push('Admission date is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new IPDAdmission(d) : null; }
}

// ── EMERGENCY ──────────────────────────────────────────────────────────────────
export class Emergency {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.caseNumber = d.caseNumber || '';
    this.patientName = d.patientName || '';
    this.patientAge = d.patientAge || '';
    this.patientGender = d.patientGender || '';
    this.phone = d.phone || '';
    this.address = d.address || '';
    this.chiefComplaint = d.chiefComplaint || '';
    this.severity = d.severity || 'Moderate';
    this.arrivalMode = d.arrivalMode || 'Walk-in';
    this.arrivalTime = d.arrivalTime || new Date().toISOString();
    this.attendingDoctorId = d.attendingDoctorId || null;
    this.attendingDoctorName = d.attendingDoctorName || '';
    this.triageLevel = d.triageLevel || 'Yellow';
    this.status = d.status || 'Active';
    this.notes = d.notes || '';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientName?.trim()) e.push('Patient name is required');
    if (!this.chiefComplaint?.trim()) e.push('Chief complaint is required');
    if (!this.severity)            e.push('Severity is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Emergency(d) : null; }
}

// ── LAB TEST ───────────────────────────────────────────────────────────────────
export class LabTest {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.labNumber = d.labNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.testName = d.testName || '';
    this.testCode = d.testCode || '';
    this.category = d.category || '';
    this.sampleType = d.sampleType || '';
    this.sampleCollectedAt = d.sampleCollectedAt || null;
    this.reportDate = d.reportDate || null;
    this.result = d.result || '';
    this.normalRange = d.normalRange || '';
    this.unit = d.unit || '';
    this.status = d.status || 'Pending';
    this.fee = d.fee || 0;
    this.remarks = d.remarks || '';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)           e.push('Patient is required');
    if (!this.testName?.trim())    e.push('Test name is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new LabTest(d) : null; }
}

// ── PATHOLOGY ──────────────────────────────────────────────────────────────────
export class PathologyTest {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.reportNumber = d.reportNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.testType = d.testType || '';
    this.specimenType = d.specimenType || '';
    this.collectionDate = d.collectionDate || '';
    this.reportDate = d.reportDate || '';
    this.findings = d.findings || '';
    this.impression = d.impression || '';
    this.pathologist = d.pathologist || '';
    this.status = d.status || 'Pending';
    this.fee = d.fee || 0;
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)        e.push('Patient is required');
    if (!this.testType?.trim()) e.push('Test type is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new PathologyTest(d) : null; }
}

// ── RADIOLOGY ──────────────────────────────────────────────────────────────────
export class RadiologyTest {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.reportNumber = d.reportNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.modalityType = d.modalityType || '';
    this.bodyPart = d.bodyPart || '';
    this.scheduledDate = d.scheduledDate || '';
    this.conductedDate = d.conductedDate || '';
    this.findings = d.findings || '';
    this.impression = d.impression || '';
    this.radiologist = d.radiologist || '';
    this.imageUrl = d.imageUrl || null;
    this.status = d.status || 'Scheduled';
    this.fee = d.fee || 0;
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)           e.push('Patient is required');
    if (!this.modalityType?.trim()) e.push('Modality type is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new RadiologyTest(d) : null; }
}

// ── PHARMACY / MEDICINE ────────────────────────────────────────────────────────
export class Medicine {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.name = d.name || '';
    this.genericName = d.genericName || '';
    this.category = d.category || '';
    this.manufacturer = d.manufacturer || '';
    this.batchNumber = d.batchNumber || '';
    this.expiryDate = d.expiryDate || '';
    this.quantity = d.quantity || 0;
    this.minStock = d.minStock || 10;
    this.purchasePrice = d.purchasePrice || 0;
    this.sellPrice = d.sellPrice || 0;
    this.unit = d.unit || 'Tablet';
    this.composition = d.composition || '';
    this.schedule = d.schedule || '';
    this.rackNumber = d.rackNumber || '';
    this.status = d.status || 'Active';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  get isLowStock() { return this.quantity <= this.minStock; }
  get isExpired() { return this.expiryDate && new Date(this.expiryDate) < new Date(); }
  validate() {
    const e = [];
    if (!this.name?.trim())     e.push('Medicine name is required');
    if (!this.category?.trim()) e.push('Category is required');
    if (this.quantity < 0)      e.push('Quantity cannot be negative');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Medicine(d) : null; }
}

// ── BLOOD BANK ─────────────────────────────────────────────────────────────────
export class BloodUnit {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.bagNumber = d.bagNumber || '';
    this.bloodGroup = d.bloodGroup || '';
    this.rhFactor = d.rhFactor || '+';
    this.componentType = d.componentType || 'Whole Blood';
    this.donorName = d.donorName || '';
    this.donorId = d.donorId || '';
    this.collectionDate = d.collectionDate || '';
    this.expiryDate = d.expiryDate || '';
    this.volume = d.volume || 450;
    this.status = d.status || 'Available';
    this.location = d.location || '';
    this.screeningStatus = d.screeningStatus || 'Pending';
    this.issuedToPatientId = d.issuedToPatientId || null;
    this.issuedToPatientName = d.issuedToPatientName || '';
    this.issuedAt = d.issuedAt || null;
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  get fullBloodGroup() { return `${this.bloodGroup}${this.rhFactor}`; }
  validate() {
    const e = [];
    if (!this.bloodGroup)        e.push('Blood group is required');
    if (!this.collectionDate)    e.push('Collection date is required');
    if (!this.donorName?.trim()) e.push('Donor name is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new BloodUnit(d) : null; }
}

// ── OPERATION THEATER ──────────────────────────────────────────────────────────
export class Surgery {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.surgeryNumber = d.surgeryNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.surgeonId = d.surgeonId || null;
    this.surgeonName = d.surgeonName || '';
    this.anesthesiologist = d.anesthesiologist || '';
    this.surgeryType = d.surgeryType || '';
    this.theatreNumber = d.theatreNumber || '';
    this.scheduledDate = d.scheduledDate || '';
    this.scheduledTime = d.scheduledTime || '';
    this.startTime = d.startTime || '';
    this.endTime = d.endTime || '';
    this.duration = d.duration || '';
    this.anesthesiaType = d.anesthesiaType || '';
    this.priority = d.priority || 'Elective';
    this.preOpDiagnosis = d.preOpDiagnosis || '';
    this.postOpDiagnosis = d.postOpDiagnosis || '';
    this.complications = d.complications || '';
    this.status = d.status || 'Scheduled';
    this.fee = d.fee || 0;
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)         e.push('Patient is required');
    if (!this.surgeonId)         e.push('Surgeon is required');
    if (!this.surgeryType?.trim()) e.push('Surgery type is required');
    if (!this.scheduledDate)     e.push('Date is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Surgery(d) : null; }
}

// ── AMBULANCE ──────────────────────────────────────────────────────────────────
export class Ambulance {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.vehicleNumber = d.vehicleNumber || '';
    this.vehicleType = d.vehicleType || 'Basic';
    this.model = d.model || '';
    this.driverName = d.driverName || '';
    this.driverPhone = d.driverPhone || '';
    this.paramedic = d.paramedic || '';
    this.lastMaintenance = d.lastMaintenance || '';
    this.nextMaintenance = d.nextMaintenance || '';
    this.status = d.status || 'Available';
    this.currentLocation = d.currentLocation || '';
    this.features = d.features || [];
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.vehicleNumber?.trim()) e.push('Vehicle number is required');
    if (!this.driverName?.trim())    e.push('Driver name is required');
    if (!this.driverPhone?.trim())   e.push('Driver phone is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Ambulance(d) : null; }
}

// ── ICU ────────────────────────────────────────────────────────────────────────
export class ICUAdmission {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.admissionNumber = d.admissionNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.bedNumber = d.bedNumber || '';
    this.admissionDate = d.admissionDate || '';
    this.admissionTime = d.admissionTime || '';
    this.dischargeDate = d.dischargeDate || null;
    this.diagnosis = d.diagnosis || '';
    this.ventilatorRequired = d.ventilatorRequired || false;
    this.condition = d.condition || 'Critical';
    this.status = d.status || 'Active';
    this.nurseAssigned = d.nurseAssigned || '';
    this.notes = d.notes || '';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)     e.push('Patient is required');
    if (!this.doctorId)      e.push('Doctor is required');
    if (!this.admissionDate) e.push('Admission date is required');
    if (!this.bedNumber?.trim()) e.push('Bed number is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new ICUAdmission(d) : null; }
}

// ── MATERNITY ──────────────────────────────────────────────────────────────────
export class MaternityCase {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.caseNumber = d.caseNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.admissionDate = d.admissionDate || '';
    this.expectedDueDate = d.expectedDueDate || '';
    this.deliveryDate = d.deliveryDate || null;
    this.deliveryType = d.deliveryType || '';
    this.babyGender = d.babyGender || '';
    this.babyWeight = d.babyWeight || '';
    this.apgarScore = d.apgarScore || '';
    this.complications = d.complications || '';
    this.status = d.status || 'Admitted';
    this.wardId = d.wardId || null;
    this.bedNumber = d.bedNumber || '';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)      e.push('Patient is required');
    if (!this.doctorId)       e.push('Doctor is required');
    if (!this.admissionDate)  e.push('Admission date is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new MaternityCase(d) : null; }
}

// ── BILLING ────────────────────────────────────────────────────────────────────
export class HospitalBill {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.billNumber = d.billNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.patientPhone = d.patientPhone || '';
    this.billDate = d.billDate || new Date().toISOString().split('T')[0];
    this.dueDate = d.dueDate || '';
    this.visitType = d.visitType || 'OPD';
    this.visitId = d.visitId || null;
    this.items = d.items || [];
    this.subtotal = d.subtotal || 0;
    this.discount = d.discount || 0;
    this.tax = d.tax || 0;
    this.totalAmount = d.totalAmount || 0;
    this.paidAmount = d.paidAmount || 0;
    this.paymentMethod = d.paymentMethod || '';
    this.status = d.status || 'Unpaid';
    this.notes = d.notes || '';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  get balance() { return this.totalAmount - this.paidAmount; }
  validate() {
    const e = [];
    if (!this.patientId)       e.push('Patient is required');
    if (!this.items?.length)   e.push('At least one billing item is required');
    if (!this.totalAmount)     e.push('Total amount is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new HospitalBill(d) : null; }
}

// ── INSURANCE ──────────────────────────────────────────────────────────────────
export class InsuranceClaim {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.claimNumber = d.claimNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.billId = d.billId || null;
    this.billNumber = d.billNumber || '';
    this.insuranceProvider = d.insuranceProvider || '';
    this.policyNumber = d.policyNumber || '';
    this.claimDate = d.claimDate || '';
    this.claimAmount = d.claimAmount || 0;
    this.approvedAmount = d.approvedAmount || 0;
    this.rejectedReason = d.rejectedReason || '';
    this.documents = d.documents || [];
    this.status = d.status || 'Pending';
    this.settledDate = d.settledDate || null;
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)               e.push('Patient is required');
    if (!this.insuranceProvider?.trim()) e.push('Insurance provider is required');
    if (!this.policyNumber?.trim())    e.push('Policy number is required');
    if (!this.claimAmount)             e.push('Claim amount is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new InsuranceClaim(d) : null; }
}

// ── MEDICAL RECORD ─────────────────────────────────────────────────────────────
export class MedicalRecord {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.recordNumber = d.recordNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.recordDate = d.recordDate || '';
    this.recordType = d.recordType || 'Consultation';
    this.diagnosis = d.diagnosis || '';
    this.treatment = d.treatment || '';
    this.prescription = d.prescription || '';
    this.labResults = d.labResults || '';
    this.imagingResults = d.imagingResults || '';
    this.surgeryNotes = d.surgeryNotes || '';
    this.followUp = d.followUp || '';
    this.attachments = d.attachments || [];
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)           e.push('Patient is required');
    if (!this.recordType)          e.push('Record type is required');
    if (!this.recordDate)          e.push('Record date is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new MedicalRecord(d) : null; }
}

// ── PRESCRIPTION ───────────────────────────────────────────────────────────────
export class Prescription {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.rxNumber = d.rxNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.doctorId = d.doctorId || null;
    this.doctorName = d.doctorName || '';
    this.prescriptionDate = d.prescriptionDate || '';
    this.diagnosis = d.diagnosis || '';
    this.medicines = d.medicines || [];
    this.instructions = d.instructions || '';
    this.followUpDate = d.followUpDate || '';
    this.status = d.status || 'Active';
    this.dispensed = d.dispensed || false;
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)       e.push('Patient is required');
    if (!this.doctorId)        e.push('Doctor is required');
    if (!this.medicines?.length) e.push('At least one medicine is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new Prescription(d) : null; }
}

// ── VITAL SIGNS ────────────────────────────────────────────────────────────────
export class VitalSign {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.recordedBy = d.recordedBy || '';
    this.recordedAt = d.recordedAt || new Date().toISOString();
    this.temperature = d.temperature || '';
    this.temperatureUnit = d.temperatureUnit || '°F';
    this.bloodPressureSystolic = d.bloodPressureSystolic || '';
    this.bloodPressureDiastolic = d.bloodPressureDiastolic || '';
    this.pulseRate = d.pulseRate || '';
    this.respiratoryRate = d.respiratoryRate || '';
    this.oxygenSaturation = d.oxygenSaturation || '';
    this.weight = d.weight || '';
    this.height = d.height || '';
    this.bmi = d.bmi || '';
    this.bloodSugar = d.bloodSugar || '';
    this.notes = d.notes || '';
    this.companyId = d.companyId || null;
  }
  validate() {
    const e = [];
    if (!this.patientId) e.push('Patient is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new VitalSign(d) : null; }
}

// ── DIET / NUTRITION ───────────────────────────────────────────────────────────
export class DietPlan {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.planNumber = d.planNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.dietitianId = d.dietitianId || null;
    this.dietitianName = d.dietitianName || '';
    this.startDate = d.startDate || '';
    this.endDate = d.endDate || '';
    this.dietType = d.dietType || 'Regular';
    this.calories = d.calories || '';
    this.protein = d.protein || '';
    this.carbohydrates = d.carbohydrates || '';
    this.fats = d.fats || '';
    this.restrictions = d.restrictions || '';
    this.mealPlan = d.mealPlan || { breakfast: '', lunch: '', dinner: '', snacks: '' };
    this.notes = d.notes || '';
    this.status = d.status || 'Active';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientId)      e.push('Patient is required');
    if (!this.startDate)      e.push('Start date is required');
    if (!this.dietType?.trim()) e.push('Diet type is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new DietPlan(d) : null; }
}

// ── MORTALITY ──────────────────────────────────────────────────────────────────
export class MortalityRecord {
  constructor(d = {}) {
    this.id = d.id || d._id || null;
    this.caseNumber = d.caseNumber || '';
    this.patientId = d.patientId || null;
    this.patientName = d.patientName || '';
    this.patientAge = d.patientAge || '';
    this.patientGender = d.patientGender || '';
    this.admissionDate = d.admissionDate || '';
    this.deathDate = d.deathDate || '';
    this.deathTime = d.deathTime || '';
    this.causeOfDeath = d.causeOfDeath || '';
    this.mannerOfDeath = d.mannerOfDeath || '';
    this.attendingDoctorId = d.attendingDoctorId || null;
    this.attendingDoctorName = d.attendingDoctorName || '';
    this.wardId = d.wardId || null;
    this.wardName = d.wardName || '';
    this.autopsyRequired = d.autopsyRequired || false;
    this.deathCertificateNumber = d.deathCertificateNumber || '';
    this.relativeNotified = d.relativeNotified || false;
    this.notes = d.notes || '';
    this.companyId = d.companyId || null;
    this.createdAt = d.createdAt || null;
  }
  validate() {
    const e = [];
    if (!this.patientName?.trim())   e.push('Patient name is required');
    if (!this.deathDate)             e.push('Death date is required');
    if (!this.causeOfDeath?.trim())  e.push('Cause of death is required');
    if (!this.attendingDoctorId)     e.push('Attending doctor is required');
    return { isValid: !e.length, errors: e };
  }
  toJSON() { return { ...this }; }
  static fromJSON(d) { return d ? new MortalityRecord(d) : null; }
}

export default {
  Patient, Doctor, Nurse, Appointment, Department, Ward, Bed,
  OPDVisit, IPDAdmission, Emergency, LabTest, PathologyTest,
  RadiologyTest, Medicine, BloodUnit, Surgery, Ambulance,
  ICUAdmission, MaternityCase, HospitalBill, InsuranceClaim,
  MedicalRecord, Prescription, VitalSign, DietPlan, MortalityRecord,
};